import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Widgets from "../../../widgets";
import { useTranslation } from "react-i18next";
import strings from "../../../utils/strings";
import Icons from "../../../Assets/Icons";
import { useNavigate } from "react-router-dom";
import "./additionalBilling.css";
import { getBillingRequests } from "../../../modules/NursignCare/AdditonalConsumable/AddConsumableThunk";

const TAB_STATUS = {
  outstanding: "OUTSTANDING",
  settled: "COMPLETED,ABSCONDED",
};

const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getFullYear()}, ${d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })}`;
};

const AdditionalBillingList = () => {
  const { t } = useTranslation();
  const nav = useNavigate();
  const dispatch = useDispatch();
  const billingData = useSelector((s) => s.addConsumable?.billingRequestsSuccess);

  const breadcrumbs = ["nursingCare", "additionalBilling"];
  const paths = [strings.navigation.nursingCareManageService, strings.navigation.additionalBilling];

  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState("outstanding");

  useEffect(() => {
    dispatch(getBillingRequests({ page: currentPage, limit: 10, status: TAB_STATUS[activeTab] }));
  }, [activeTab, currentPage, dispatch]);

  const rows = useMemo(() => {
    const requests = billingData?.data?.requests || [];
    return requests.flatMap((req) =>
      (req.sessions || []).map((sess) => {
        const consumables = sess.additionalConsumables || [];
        const totalAmount = consumables.reduce((sum, c) => sum + parseFloat(c.consumable?.price || 0) * (c.quantity || 0), 0);
        const totalQty = consumables.reduce((sum, c) => sum + (c.quantity || 0), 0);
        const firstName = consumables[0]?.consumable?.itemName?.en || "-";
        const itemLabel = consumables.length > 1 ? `${firstName} +${consumables.length - 1} more` : firstName;
        const status = sess.additionalConsumablePaymentStatus;
        const paid = status === "COMPLETED" ? totalAmount : 0;
        const remaining = totalAmount - paid;
        return {
          id: sess._id,
          reqId: req._id,
          name: req.patient?.fullName || "-",
          phone: req.patient ? `+${req.patient.countryCode} ${req.patient.phoneNumber}` : "-",
          photo: req.patient?.profilePhoto || "https://i.pravatar.cc/40",
          hn: req.patient?.hn || "-",
          dateTime: formatDate(sess.date),
          nurseId: sess.nurse?.employeeID || "-",
          nurseName: sess.nurse?.fullName || "-",
          itemName: itemLabel,
          itemQuantity: totalQty,
          total: totalAmount,
          paid,
          remaining,
          status,
          _req: req,
          _sess: sess,
        };
      })
    );
  }, [billingData]);

  const totalPages = Math.max(1, billingData?.data?.totalPages || 1);

  const onTab = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const renderPagination = () => {
    const pages = [];
    if (totalPages <= 4) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 2) pages.push(1, 2, { type: "ellipsis" }, totalPages);
      else if (currentPage >= totalPages - 2) pages.push(1, { type: "ellipsis" }, totalPages - 1, totalPages);
      else pages.push(1, { type: "ellipsis" }, currentPage, { type: "ellipsis" }, totalPages);
    }
    return pages;
  };

  const statusLabel = (s) => {
    if (s === "OUTSTANDING") return "Outstanding";
    if (s === "COMPLETED") return "Paid";
    if (s === "ABSCONDED") return "Absconded";
    return s || "-";
  };

  const statusClass = (s) => {
    if (s === "OUTSTANDING") return "status-outstanding";
    if (s === "COMPLETED") return "status-paid";
    return "status-absconded";
  };

  const statusIcon = (s) => {
    if (s === "OUTSTANDING") return Icons.spinnerSolid1;
    if (s === "COMPLETED") return Icons.checkSolid;
    return Icons.abIcon;
  };

  return (
    <div className="dp-container">
      <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />

      <div className="department-heading">{t("additionalBillingPaymentStatus")}</div>

      <div className="billing-tabs">
        <button type="button" className={`billing-tab ${activeTab === "outstanding" ? "active" : ""}`} onClick={() => onTab("outstanding")}>
          {t("outstandingPayments")}
        </button>
        <button type="button" className={`billing-tab ${activeTab === "settled" ? "active" : ""}`} onClick={() => onTab("settled")}>
          {t("settledPayments")}
        </button>
      </div>

      <div className="dp-list-box">
        <table className="billing-table">
          <thead>
            <tr>
              <th>{t("patientDetails")}</th>
              <th>{t("hn")}</th>
              <th>{t("dateTime")}</th>
              <th>{t("chargedByNurse")}</th>
              <th>{t("itemName")}</th>
              <th>{t("itemQuantity")}</th>
              <th>{t("totalAmount")}</th>
              <th>{t("paidAmount")}</th>
              <th>{t("remainingBalance")}</th>
              <th>{t("status")}</th>
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ textAlign: "center", padding: "24px", color: "#888" }}>
                  No records found
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} onClick={() => nav(strings.navigation.paymentDetails, { state: { request: row._req, session: row._sess } })} style={{ cursor: "pointer" }} className="clickable-row">
                  <td>
                    <div className="patient-cell">
                      <img
                        src={row.photo}
                        alt="avatar"
                        className="patient-avatar clickable"
                        style={{ cursor: "pointer" }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div>
                        <div className="patient-name">{row.name}</div>
                        <div className="patient-phone">{row.phone}</div>
                      </div>
                    </div>
                  </td>
                  <td>{row.hn}</td>
                  <td>{row.dateTime}</td>
                  <td>
                    <div>
                      <div className="nurse-id">{row.nurseId}</div>
                      <div className="nurse-name">{row.nurseName}</div>
                    </div>
                  </td>
                  <td>{row.itemName}</td>
                  <td>{row.itemQuantity}</td>
                  <td>${row.total.toFixed(2)}</td>
                  <td>${row.paid.toFixed(2)}</td>
                  <td>${row.remaining.toFixed(2)}</td>
                  <td>
                    <span className={`status-pill ${statusClass(row.status)}`}>
                      <img src={statusIcon(row.status)} alt="" className="status-icon" />
                      {statusLabel(row.status)}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="department-pagination-list">
          <button onClick={() => setCurrentPage((p) => p - 1)} disabled={currentPage === 1} className="department-pagination-prev-list">
            <img src={Icons.leftArrow} alt="<" />
          </button>

          {renderPagination().map((page, index) => (
            <button
              key={index}
              onClick={() => typeof page === "number" && setCurrentPage(page)}
              disabled={page === currentPage || page?.type === "ellipsis"}
              className={`department-pagination-btn-list ${page === currentPage ? "active-list" : ""} ${page?.type === "ellipsis" ? "ellipse" : ""}`}
            >
              {typeof page === "number" ? page : <img src={Icons.extraIcon} alt="..." className="department-extra-icon" />}
            </button>
          ))}

          <button onClick={() => setCurrentPage((p) => p + 1)} disabled={currentPage === totalPages} className="department-pagination-next-list">
            <img src={Icons.rightArrow} alt=">" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdditionalBillingList;
